import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  Lock, 
  Mail, 
  Phone, 
  MapPin, 
  CheckCircle,
  Printer,
  Globe,
  FileText,
  UserCheck,
  Database,
  Bell,
  Building,
  Users
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

function Privacy() {
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState('');

  useEffect(() => {
    const today = new Date();
    const formattedDate = today.toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    setCurrentDate(formattedDate);
  }, []);

  // KANADX company information
  const companyInfo = {
    name: "KANADX",
    fullName: t('privacy.company.fullName'),
    website: "www.kanadx.com",
    email: "info@kanadx.com",
    privacyEmail: "privacy@kanadx.com",
    canadaPhonePrimary: "+1 833-370-0333",
    canadaPhoneSecondary: "+1 (416) 700-7091",
    indiaPhone: "+91 98500 83751",
    canadaAddress: "338 Queen St E # 207 Brampton, ON L6V 1C4 Canada",
    indiaAddress: "Office no. 409, 4th Floor, Rajdhani Complex, Pune - Satara Rd, near Shankar Maharaj Math, KK Market, Balaji Nagar, Pune, Maharashtra 411043",
    indianPartner: "Zplus Cyber Secure Technology Pvt Ltd, Pune",
    privacyOfficer: "KANADX Privacy Team"
  };

  // Translation data
  const translations = {
    hero: {
      title: t('privacy.hero.title'),
      subtitle: t('privacy.hero.subtitle'),
      tagline: t('privacy.hero.tagline'),
      description: t('privacy.hero.description')
    },
    sections: {
      introduction: {
        title: t('privacy.sections.introduction.title'),
        content: t('privacy.sections.introduction.content', {
          partnerName: companyInfo.indianPartner
        })
      },
      scope: {
        title: t('privacy.sections.scope.title'),
        description: t('privacy.sections.scope.description', { companyName: companyInfo.name }),
        items: t('privacy.sections.scope.items', { returnObjects: true })
      },
      informationCollection: {
        title: t('privacy.sections.informationCollection.title'),
        personalInfoTitle: t('privacy.sections.informationCollection.personalInfoTitle'),
        visitorsTitle: t('privacy.sections.informationCollection.visitorsTitle'),
        visitorsItems: t('privacy.sections.informationCollection.visitorsItems', { returnObjects: true }),
        clientsTitle: t('privacy.sections.informationCollection.clientsTitle'),
        services: {
          softwareDev: {
            title: t('privacy.sections.informationCollection.services.softwareDev.title'),
            items: t('privacy.sections.informationCollection.services.softwareDev.items', { returnObjects: true })
          },
          digitalMarketing: {
            title: t('privacy.sections.informationCollection.services.digitalMarketing.title'),
            items: t('privacy.sections.informationCollection.services.digitalMarketing.items', { returnObjects: true })
          },
          cybersecurity: {
            title: t('privacy.sections.informationCollection.services.cybersecurity.title'),
            items: t('privacy.sections.informationCollection.services.cybersecurity.items', { returnObjects: true })
          }
        },
        nonPersonalInfoTitle: t('privacy.sections.informationCollection.nonPersonalInfoTitle'),
        nonPersonalInfoContent: t('privacy.sections.informationCollection.nonPersonalInfoContent')
      },
      consent: {
        title: t('privacy.sections.consent.title'),
        content: t('privacy.sections.consent.content'),
        withdrawTitle: t('privacy.sections.consent.withdrawTitle'),
        withdrawContent: t('privacy.sections.consent.withdrawContent')
      },
      rights: {
        title: t('privacy.sections.rights.title'),
        description: t('privacy.sections.rights.description'),
        items: t('privacy.sections.rights.items', { returnObjects: true }),
        exerciseRights: t('privacy.sections.rights.exerciseRights')
      },
      dataSecurity: {
        title: t('privacy.sections.dataSecurity.title'),
        orgTitle: t('privacy.sections.dataSecurity.orgTitle'),
        orgItems: t('privacy.sections.dataSecurity.orgItems', { returnObjects: true }),
        clientTitle: t('privacy.sections.dataSecurity.clientTitle'),
        clientItems: t('privacy.sections.dataSecurity.clientItems', { returnObjects: true })
      },
      complaint: {
        title: t('privacy.sections.complaint.title'),
        content: t('privacy.sections.complaint.content'),
        opcTitle: t('privacy.sections.complaint.opcTitle'),
        opcAddress: t('privacy.sections.complaint.opcAddress'),
        opcPhone: t('privacy.sections.complaint.opcPhone'),
        opcWebsite: t('privacy.sections.complaint.opcWebsite')
      },
      compliance: {
        title: t('privacy.sections.compliance.title'),
        frameworks: t('privacy.sections.compliance.frameworks', { returnObjects: true })
      }
    },
    footer: {
      tagline: t('privacy.footer.tagline'),
      statement: t('privacy.footer.statement', { companyName: companyInfo.name }),
      print: t('privacy.footer.print'),
      rights: t('privacy.footer.rights'),
      indianPartner: t('privacy.footer.indianPartner')
    },
    buttons: {
      print: t('privacy.buttons.print')
    },
    labels: {
      address: t('privacy.labels.address'),
      tollFree: t('privacy.labels.tollFree'),
      website: t('privacy.labels.website')
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header Section - Red/Black Theme */}
      <div className="bg-gradient-to-r from-black via-red-900 to-black text-white py-12">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row items-center justify-center mb-6">
            <div className="flex items-center mb-4 md:mb-0">
              <Shield className="w-16 h-16 mr-4 text-red-500" />
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-white">
                  {translations.hero.title}
                </h1>
                <div className="flex items-center mt-2">
                  <Building className="w-4 h-4 mr-2 text-red-400" />
                  <p className="text-red-300 font-medium">{companyInfo.fullName}</p>
                </div>
              </div>
            </div>
          </div>
          <p className="text-center text-red-200 text-lg mb-8">
            {translations.hero.tagline}
          </p>
          
          <div className="mt-8 bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 max-w-2xl mx-auto border border-red-900">
            <div>
              <p className="text-center text-gray-300">
                {translations.hero.description}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 md:px-6 py-12 max-w-6xl">
        {/* Introduction */}
        <section className="mb-12 bg-gray-800 rounded-2xl shadow-lg p-8 border border-red-900">
          <div className="flex items-start mb-6">
            <div className="bg-red-900 p-3 rounded-lg mr-4">
              <Globe className="w-6 h-6 text-red-300" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white mb-4">
                {translations.sections.introduction.title}
              </h2>
              <p className="text-gray-300 leading-relaxed">
                {translations.sections.introduction.content}
              </p>
            </div>
          </div>
        </section>

        {/* Scope and Application */}
        <section className="mb-12 bg-gray-800 rounded-2xl shadow-lg p-8 border border-red-900">
          <h2 className="text-2xl font-bold text-white mb-6 border-b border-red-900 pb-3">
            {translations.sections.scope.title}
          </h2>
          <p className="text-gray-300 mb-6">
            {translations.sections.scope.description}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {translations.sections.scope.items.map((item, index) => (
              <div key={index} className="bg-gray-900 rounded-lg p-4 border border-red-900 hover:border-red-700 transition-colors">
                <div className="w-8 h-8 bg-red-900 rounded-full flex items-center justify-center mb-3">
                  <span className="text-red-300 font-bold">{index + 1}</span>
                </div>
                <p className="text-gray-200">{item}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Information We Collect */}
        <section className="mb-12 bg-gray-800 rounded-2xl shadow-lg p-8 border border-red-900">
          <h2 className="text-2xl font-bold text-white mb-6 border-b border-red-900 pb-3">
            {translations.sections.informationCollection.title}
          </h2>
          
          {/* Personal Information */}
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <div className="bg-red-900 p-2 rounded-lg mr-3">
                <UserCheck className="w-5 h-5 text-red-300" />
              </div>
              <h3 className="text-xl font-semibold text-gray-200">
                {translations.sections.informationCollection.personalInfoTitle}
              </h3>
            </div>
            
            {/* For All Visitors */}
            <div className="mb-6 bg-gray-900 rounded-xl p-6 border border-red-900">
              <h4 className="text-lg font-semibold text-red-300 mb-4 flex items-center">
                <span className="bg-red-900 w-6 h-6 rounded-full flex items-center justify-center mr-2 text-sm">①</span>
                {translations.sections.informationCollection.visitorsTitle}
              </h4>
              <ul className="space-y-2">
                {translations.sections.informationCollection.visitorsItems.map((item, idx) => (
                  <li key={idx} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Service-Specific Information */}
            <div className="bg-gray-900 rounded-xl p-6 border border-red-900">
              <h4 className="text-lg font-semibold text-red-300 mb-4">
                {translations.sections.informationCollection.clientsTitle}
              </h4>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Software Development */}
                <div className="bg-black rounded-lg p-5 border border-red-900 hover:border-red-700 transition-colors">
                  <div className="flex items-center mb-3">
                    <div className="bg-red-900 p-2 rounded mr-3">
                      <Database className="w-5 h-5 text-red-300" />
                    </div>
                    <h5 className="font-semibold text-red-300">
                      {translations.sections.informationCollection.services.softwareDev.title}
                    </h5>
                  </div>
                  <ul className="space-y-2 text-sm">
                    {translations.sections.informationCollection.services.softwareDev.items.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <div className="w-2 h-2 bg-red-500 rounded-full mt-1.5 mr-2"></div>
                        <span className="text-gray-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Digital Marketing */}
                <div className="bg-black rounded-lg p-5 border border-red-900 hover:border-red-700 transition-colors">
                  <div className="flex items-center mb-3">
                    <div className="bg-red-900 p-2 rounded mr-3">
                      <Bell className="w-5 h-5 text-red-300" />
                    </div>
                    <h5 className="font-semibold text-red-300">
                      {translations.sections.informationCollection.services.digitalMarketing.title}
                    </h5>
                  </div>
                  <ul className="space-y-2 text-sm">
                    {translations.sections.informationCollection.services.digitalMarketing.items.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <div className="w-2 h-2 bg-red-500 rounded-full mt-1.5 mr-2"></div>
                        <span className="text-gray-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Cybersecurity */}
                <div className="bg-black rounded-lg p-5 border border-red-900 hover:border-red-700 transition-colors">
                  <div className="flex items-center mb-3">
                    <div className="bg-red-900 p-2 rounded mr-3">
                      <Lock className="w-5 h-5 text-red-300" />
                    </div>
                    <h5 className="font-semibold text-red-300">
                      {translations.sections.informationCollection.services.cybersecurity.title}
                    </h5>
                  </div>
                  <ul className="space-y-2 text-sm">
                    {translations.sections.informationCollection.services.cybersecurity.items.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <div className="w-2 h-2 bg-red-500 rounded-full mt-1.5 mr-2"></div>
                        <span className="text-gray-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Non-Personal Information */}
          <div className="bg-gray-900 rounded-xl p-6 border border-red-900">
            <h3 className="text-xl font-semibold text-gray-200 mb-3">
              {translations.sections.informationCollection.nonPersonalInfoTitle}
            </h3>
            <p className="text-gray-300">
              {translations.sections.informationCollection.nonPersonalInfoContent}
            </p>
          </div>
        </section>

        {/* Consent Section */}
        <section className="mb-12 bg-gradient-to-r from-gray-900 to-black rounded-2xl shadow-lg p-8 border border-red-900">
          <h2 className="text-2xl font-bold text-white mb-6">
            {translations.sections.consent.title}
          </h2>
          <div className="bg-gray-800 rounded-xl p-6 mb-6 border border-red-900">
            <p className="text-gray-300 mb-4">
              {translations.sections.consent.content}
            </p>
          </div>
          <div className="bg-red-900/50 border-l-4 border-red-600 p-6 rounded-r-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <Bell className="h-6 w-6 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-semibold text-red-300">
                  {translations.sections.consent.withdrawTitle}
                </h3>
                <p className="mt-2 text-red-200">
                  <strong>{translations.sections.consent.withdrawContent}</strong>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Individual Rights */}
        <section className="mb-12 bg-gray-800 rounded-2xl shadow-lg p-8 border border-red-900">
          <h2 className="text-2xl font-bold text-white mb-6 border-b border-red-900 pb-3">
            {translations.sections.rights.title}
          </h2>
          <p className="text-gray-300 mb-6">
            {translations.sections.rights.description}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {translations.sections.rights.items.map((right, index) => (
              <div key={index} className="bg-gray-900 rounded-xl p-6 border border-red-900 hover:border-red-700 transition-colors">
                <div className="flex items-center mb-4">
                  <div className="bg-red-900 p-3 rounded-lg mr-4">
                    <CheckCircle className="w-6 h-6 text-red-300" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">{right.title}</h3>
                </div>
                <p className="text-gray-300">{right.desc}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-8 bg-gray-900 rounded-xl p-6 border border-red-900">
            <p className="text-gray-300">
              {translations.sections.rights.exerciseRights}
            </p>
          </div>
        </section>

        {/* Data Security */}
        <section className="mb-12 bg-gray-800 rounded-2xl shadow-lg p-8 border border-red-900">
          <h2 className="text-2xl font-bold text-white mb-6 border-b border-red-900 pb-3">
            {translations.sections.dataSecurity.title}
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-gray-900 rounded-xl p-6 border border-red-900">
              <div className="flex items-center mb-4">
                <Lock className="w-8 h-8 text-red-400 mr-3" />
                <h3 className="text-xl font-semibold text-red-300">
                  {translations.sections.dataSecurity.orgTitle}
                </h3>
              </div>
              <ul className="space-y-3">
                {translations.sections.dataSecurity.orgItems.map((item, idx) => (
                  <li key={idx} className="flex items-start">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3"></div>
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gray-900 rounded-xl p-6 border border-red-900">
              <div className="flex items-center mb-4">
                <Shield className="w-8 h-8 text-red-400 mr-3" />
                <h3 className="text-xl font-semibold text-red-300">
                  {translations.sections.dataSecurity.clientTitle}
                </h3>
              </div>
              <ul className="space-y-3">
                {translations.sections.dataSecurity.clientItems.map((item, idx) => (
                  <li key={idx} className="flex items-start">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3"></div>
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Complaint Procedure */}
        <section className="mb-12 bg-gray-800 rounded-2xl shadow-lg p-8 border border-red-900">
          <h2 className="text-2xl font-bold text-white mb-6 border-b border-red-900 pb-3">
            {translations.sections.complaint.title}
          </h2>
          
          <div className="bg-red-900/50 border-l-4 border-red-600 p-6 rounded-r-lg mb-6">
            <p className="text-gray-300 mb-4">
              {translations.sections.complaint.content}
            </p>
          </div>
          
          <div className="bg-gray-900 rounded-xl p-6 border border-red-900">
            <h4 className="text-lg font-semibold text-white mb-4">
              {translations.sections.complaint.opcTitle}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-red-300 text-sm">
                  {translations.labels.address}
                </p>
                <p className="text-gray-200">
                  {translations.sections.complaint.opcAddress}
                </p>
              </div>
              <div>
                <p className="text-red-300 text-sm">
                  {translations.labels.tollFree}
                </p>
                <p className="text-white font-semibold">
                  {translations.sections.complaint.opcPhone}
                </p>
              </div>
              <div>
                <p className="text-red-300 text-sm">
                  {translations.labels.website}
                </p>
                <a href="https://www.priv.gc.ca" target="_blank" rel="noopener noreferrer" 
                   className="text-red-400 hover:text-red-300 font-semibold">
                  {translations.sections.complaint.opcWebsite}
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Additional Compliance */}
        <section className="mb-12 bg-gradient-to-r from-gray-900 to-black rounded-2xl shadow-lg p-8 border border-red-900">
          <h2 className="text-2xl font-bold text-white mb-6">
            {translations.sections.compliance.title}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {translations.sections.compliance.frameworks.map((framework, idx) => (
              <div key={idx} className="bg-gray-900 rounded-xl p-5 border border-red-900 hover:border-red-700 transition-colors">
                <div className="w-12 h-12 bg-red-900 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-red-300" />
                </div>
                <h3 className="font-bold text-white mb-2">{framework.name}</h3>
                <p className="text-sm text-gray-300">{framework.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Footer */}
        <div className="bg-black rounded-2xl p-8 text-center border border-red-900 shadow-lg">
          <div className="mb-6">
            <h3 className="text-2xl font-bold text-white mb-2">
              {companyInfo.name}
            </h3>
            <p className="text-red-300 font-medium">
              {translations.footer.tagline}
            </p>
            <p className="text-gray-400 mt-2">
              {t('privacy.company.division')}
            </p>
          </div>
          
          <p className="text-gray-300 mb-8 text-lg">
            <strong>
              {translations.footer.statement}
            </strong>
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6">
            <button 
              onClick={() => window.print()}
              className="flex items-center bg-red-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-600 transition-colors shadow-lg border border-red-800"
            >
              <Printer className="w-5 h-5 mr-2" />
              {translations.buttons.print}
            </button>
          </div>
          
          <div className="mt-8 pt-6 border-t border-red-900">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} {companyInfo.fullName}. {translations.footer.rights}
            </p>
            <p className="text-gray-600 text-xs mt-2">
              {translations.footer.indianPartner}: {companyInfo.indianPartner}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Privacy;